import { API_BASE } from "./apiBase.ts";

export const ADMIN_PREVIEW_CONTEXT_EVENT = "reporttool:admin-preview-context-changed";

export function getAdminTenantHeader() {
  const tenantId = window.sessionStorage.getItem("reporttool:admin-tenant-id");
  const roleId = window.sessionStorage.getItem("reporttool:admin-role-id");
  return tenantId
    ? {
        "X-Reporttool-Admin-Tenant": tenantId,
        ...(roleId ? { "X-Reporttool-Admin-Role": roleId } : {}),
      }
    : {};
}

export function getAdminPreviewContextKey() {
  const tenantId = window.sessionStorage.getItem("reporttool:admin-tenant-id") || "none";
  const roleId = window.sessionStorage.getItem("reporttool:admin-role-id") || "none";
  return `${tenantId}:${roleId}`;
}

export function setAdminPreviewContext(tenantId: number, roleId: number) {
  window.sessionStorage.setItem("reporttool:admin-tenant-id", String(tenantId));
  window.sessionStorage.setItem("reporttool:admin-role-id", String(roleId));
  window.dispatchEvent(new Event(ADMIN_PREVIEW_CONTEXT_EVENT));
}

export function clearAdminPreviewContext() {
  window.sessionStorage.removeItem("reporttool:admin-tenant-id");
  window.sessionStorage.removeItem("reporttool:admin-role-id");
  window.dispatchEvent(new Event(ADMIN_PREVIEW_CONTEXT_EVENT));
}

function authHeaders(accessToken: string, email?: string, json = false) {
  return {
    ...(json ? { "Content-Type": "application/json" } : {}),
    Authorization: `Bearer ${accessToken}`,
    ...(email ? { "X-User-Email": email } : {}),
    ...getAdminTenantHeader(),
  };
}

export type PersonalMenuAssignment = {
  saved_item_id: number;
  kind: "card" | "dashboard";
  metabase_id: number;
  title: string;
  menu_section: string;
};

export async function listPersonalMenuAssignments(accessToken: string, email?: string) {
  const resp = await fetch(`${API_BASE}/api/me/personal-menu`, {
    headers: authHeaders(accessToken, email),
    credentials: "include",
    cache: "no-store",
  });
  if (!resp.ok) throw new Error(`personal menu ${resp.status}: ${await resp.text()}`);
  const data = await resp.json();
  return (data.items || []) as PersonalMenuAssignment[];
}

export async function setPersonalMenuAssignment(
  accessToken: string,
  savedItemId: number,
  menuSection: string | null,
  email?: string,
) {
  const resp = await fetch(`${API_BASE}/api/me/personal-menu/${savedItemId}`, {
    method: "PUT",
    headers: authHeaders(accessToken, email, true),
    credentials: "include",
    body: JSON.stringify({ menu_section: menuSection }),
  });
  if (!resp.ok) throw new Error(`personal menu ${resp.status}: ${await resp.text()}`);
  return resp.json() as Promise<{
    saved_item_id: number;
    personal_menu_section: string | null;
    show_in_personal_menu: boolean;
  }>;
}

export async function createSavedItem(
  accessToken: string,
  body: { kind: "card" | "dashboard"; metabase_id: number; title: string },
  email?: string
) {
  const resp = await fetch(`${API_BASE}/api/saved`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...(email ? { "X-User-Email": email } : {}),
      ...getAdminTenantHeader(),
    },
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`saved ${resp.status}: ${text}`);
  }
  return resp.json();
}

export async function listSavedItems(accessToken: string, kind?: string,
  email?: string, scope?: "all" | "mine") {
  const query = new URLSearchParams();
  if (kind) query.set("kind", kind);
  if (scope) query.set("scope", scope);
  const params = query.toString();
  const resp = await fetch(`${API_BASE}/api/saved${params ? `?${params}` : ""}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(email ? { "X-User-Email": email } : {}),
      ...getAdminTenantHeader(),
    },
    cache: "no-store",
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`saved ${resp.status}: ${text}`);
  }
  return resp.json();
}

export async function updateSavedItem(
  accessToken: string,
  id: number,
  body: {
    display_name?: string | null;
    favorite_group_id?: number | null;
    is_favorite?: boolean;
    personal_menu_section?: string | null;
  },
  email?: string
) {
  const resp = await fetch(`${API_BASE}/api/saved/${id}`, {
    method: "PATCH",
    headers: authHeaders(accessToken, email, true),
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`saved update ${resp.status}: ${text}`);
  }
  return resp.json();
}

export async function markSavedItemRun(accessToken: string, id: number, email?: string) {
  const resp = await fetch(`${API_BASE}/api/saved/${id}/run`, {
    method: "POST",
    headers: authHeaders(accessToken, email),
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`saved run ${resp.status}: ${text}`);
  }
  return resp.json();
}

export async function setSavedFavorite(
  accessToken: string,
  id: number,
  isFavorite: boolean,
  email?: string
) {
  const resp = await fetch(`${API_BASE}/api/saved/${id}/favorite`, {
    method: "PUT",
    headers: authHeaders(accessToken, email, true),
    body: JSON.stringify({ is_favorite: isFavorite }),
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`saved favorite ${resp.status}: ${text}`);
  }
  return resp.json();
}

export async function publishSavedItem(
  accessToken: string,
  id: number,
  body: { title: string; menu_section: string; refresh_sec?: number | null },
  email?: string
) {
  const resp = await fetch(`${API_BASE}/api/saved/${id}/publish`, {
    method: "POST",
    headers: authHeaders(accessToken, email, true),
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`saved publish ${resp.status}: ${text}`);
  }
  return resp.json();
}

export async function unpublishSavedItem(
  accessToken: string,
  id: number,
  body: { menu_section?: string } = {},
  email?: string
) {
  const resp = await fetch(`${API_BASE}/api/saved/${id}/unpublish`, {
    method: "POST",
    headers: authHeaders(accessToken, email, true),
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`saved unpublish ${resp.status}: ${text}`);
  }
  return resp.json();
}

export async function setTenantDefaultSavedItem(
  accessToken: string,
  id: number,
  body: { menu_section?: string } = {},
  email?: string
) {
  const resp = await fetch(`${API_BASE}/api/saved/${id}/tenant-default`, {
    method: "POST",
    headers: authHeaders(accessToken, email, true),
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    const text = await resp.text();
    let payload: { error?: string; detail?: string } | null = null;
    try {
      payload = JSON.parse(text);
    } catch {
      // Keep the original response text below when the server did not return JSON.
    }
    if (
      payload?.error === "saved_item_not_in_menu" ||
      payload?.detail === "published_report_required"
    ) {
      throw new Error(
        "Make this report tenant-wide before setting it as the tenant default.",
      );
    }
    throw new Error(`saved tenant default ${resp.status}: ${text}`);
  }
  return resp.json();
}

export async function clearTenantDefaultSavedItem(
  accessToken: string,
  id: number,
  body: { menu_section?: string } = {},
  email?: string
) {
  const resp = await fetch(`${API_BASE}/api/saved/${id}/tenant-default/clear`, {
    method: "POST",
    headers: authHeaders(accessToken, email, true),
    body: JSON.stringify(body),
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`saved tenant default clear ${resp.status}: ${text}`);
  }
  return resp.json();
}

export async function listSavedGroups(accessToken: string, email?: string) {
  const resp = await fetch(`${API_BASE}/api/saved-groups`, {
    headers: authHeaders(accessToken, email),
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`saved groups ${resp.status}: ${text}`);
  }
  return resp.json();
}

export async function createSavedGroup(accessToken: string, name: string, email?: string) {
  const resp = await fetch(`${API_BASE}/api/saved-groups`, {
    method: "POST",
    headers: authHeaders(accessToken, email, true),
    body: JSON.stringify({ name }),
  });

  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`saved group create ${resp.status}: ${text}`);
  }
  return resp.json();
}

export async function deleteSavedItem(accessToken: string, id: number,
  email?: string
) {
  const resp = await fetch(`${API_BASE}/api/saved/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(email ? { "X-User-Email": email } : {}),
      ...getAdminTenantHeader(),
    },
  });

  if (!resp.ok && resp.status !== 204) {
    const text = await resp.text();
    throw new Error(`saved delete ${resp.status}: ${text}`);
  }
}
